import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { taskApi } from 'api';
import TestTask from 'Components/ChecklistTasks/TestTask/TestTask';
import GapTask from 'Components/ChecklistTasks/GapTask/GapTask';
import { emptyTasks } from '../../constants/emptyChecklistTask';
import { getUserType } from '../../redux/slices/userSlice';
import { EmptyTasksType, TaskKeysType, WorkType } from '../../shared/types';
import { SummarizeTask } from '../../shared/types/task';
import ExcludesTask from '../ChecklistTasks/ExcludesTask';
import SelectOneTask from '../ChecklistTasks/SelectOneTask';
import TaskWithLink from '../ChecklistTasks/TaskWithLink';
import TextTask from '../ChecklistTasks/TextTask';
import { getIsSavedTask, setSavedTask } from 'redux/slices/flagsSlice';

// You can connect these tasks below
import MathFormulasTask from 'Components/ChecklistTasks/MathFormulasTask/MathFormulasTask';
import WordCompositionTask from 'Components/ChecklistTasks/WordCompositionTask/WordCompositionTask';
import WordStressTask from 'Components/ChecklistTasks/WordStressTask/WordStressTask';
import WordUnderlineTask from 'Components/ChecklistTasks/WordUnderlineTask/WordUnderlineTask';
import SentenceStructureTask from '../ChecklistTasks/SentenceStructureTask';
import TaskRow from 'Components/TaskRow/TaskRow';

type TaskTypeKey =
  | 'text'
  | 'link'
  | 'test'
  | 'exclude'
  | 'selectOne'
  | 'gap'
  | 'sentenceStructure'
  | 'mathFormulas'
  | 'wordComposition'
  | 'wordStress'
  | 'underline';

const taskTypeListAll = [
  { name: 'textTask', value: 'text' },
  { name: 'taskWithLink', value: 'link' },
  { name: 'testTask', value: 'test' },
  { name: 'excludesTask', value: 'exclude' },
  { name: 'selectOneTask', value: 'selectOne' },
  { name: 'sentenceStructureTask', value: 'sentenceStructure' },
  { name: 'gapTask', value: 'gap' },
  { name: 'mathFormulasTask', value: 'mathFormulas' },
  { name: 'wordCompositionTask', value: 'wordComposition' },
  { name: 'wordStressTask', value: 'wordStress' },
  { name: 'wordUnderlineTask', value: 'underline' },

  // can use this array to return all task if needed
];

const taskTypeList = [
  { name: 'textTask', value: 'text' },
  { name: 'taskWithLink', value: 'link' },
  { name: 'testTask', value: 'test' },
  { name: 'excludesTask', value: 'exclude' },
  { name: 'selectOneTask', value: 'selectOne' },
  { name: 'gapTask', value: 'gap' },
];

const componentMapping: Partial<Record<TaskTypeKey, any>> = {
  text: TextTask,
  link: TaskWithLink,
  test: TestTask,
  exclude: ExcludesTask,
  selectOne: SelectOneTask,
  gap: GapTask,
  // sentenceStructure: SentenceStructureTask,
  // mathFormulas: MathFormulasTask,
  // wordComposition: WordCompositionTask,
  // wordStress: WordStressTask,
  // underline: WordUnderlineTask,
};

export default function CheckListTasksForm({
  tasks,
  setTasks,
  currentICLId,
  updateICL,
  workType,
  onReorderingChange,
}: {
  tasks: SummarizeTask[];
  setTasks: React.Dispatch<React.SetStateAction<SummarizeTask[]>>;
  currentICLId?: string;
  updateICL: boolean;
  workType?: WorkType;
  onReorderingChange?: (isReordering: boolean) => void;
}) {
  const { t } = useTranslation();
  const userTypeSelector = useSelector(getUserType);

  const [newTaskType, setNewTaskType] = useState<string>('');
  const [isReordering, setIsReordering] = useState(false);
  const reorderingRef = useRef(false);

  const dispatch = useDispatch();

  const isSavedTask = useSelector(getIsSavedTask);

  const handleChangeNewTaskType = (taskType: keyof EmptyTasksType) => {
    setNewTaskType(taskType);
  };

  const handleAddNewTaskToChecklist = () => {
    addNewTaskToChecklist(newTaskType as keyof EmptyTasksType);
    dispatch(setSavedTask(false));
  };

  const addNewTaskToChecklist = (taskType: keyof EmptyTasksType) => {
    const id = uuidv4();
    const newTask = JSON.parse(JSON.stringify(emptyTasks[taskType]));
    const emptyTask: SummarizeTask = {
      ...newTask,
      id,
      indexNumber: 0,
    };

    setTasks(current => [
      ...current,
      { ...emptyTask, indexNumber: Math.max(-1, ...current.map(task => task.indexNumber)) + 1 },
    ]);
    setNewTaskType('');
  };

  const handleDeleteTask = (id: string) => {
    setTasks(current => current.filter(task => task.id !== id));
    dispatch(setSavedTask(true));
  };

  const handleChangeTaskByKey = (id: string, key: TaskKeysType, value: string | boolean | string[]) => {
    setTasks(current => current.map(task => (task.id === id ? { ...task, [key]: value } : task)));
  };

  const handleTaskPositionChange = async (id: string, newIndex: number) => {
    if (reorderingRef.current || !currentICLId) return;

    const currentIndex = tasks.findIndex(task => task.id === id);
    if (currentIndex === -1 || newIndex < 1 || newIndex > tasks.length || currentIndex === newIndex - 1) return;

    const updatedTasks = [...tasks];
    const [movedTask] = updatedTasks.splice(currentIndex, 1);
    updatedTasks.splice(newIndex - 1, 0, movedTask);

    const reordered = updatedTasks.map((task, indexNumber) => ({ ...task, indexNumber }));
    reorderingRef.current = true;
    setIsReordering(true);
    onReorderingChange?.(true);
    setTasks(reordered);

    try {
      await taskApi.updateTaskOrder(currentICLId, reordered);
    } catch (error) {
      const previousPositions = new Map(tasks.map(task => [task.id, task.indexNumber]));
      const previousOrder = new Map(tasks.map((task, index) => [task.id, index]));
      setTasks(current =>
        current
          .map(task => ({ ...task, indexNumber: previousPositions.get(task.id) ?? task.indexNumber }))
          .sort((a, b) => (previousOrder.get(a.id) ?? tasks.length) - (previousOrder.get(b.id) ?? tasks.length)),
      );
      toast.error(t('Something went wrong. Try again!'));
    } finally {
      reorderingRef.current = false;
      setIsReordering(false);
      onReorderingChange?.(false);
    }
  };

  return (
    <fieldset disabled={isReordering} className="min-w-0">
      <div className="!mt-8 flex flex-col !gap-5 lg:!mt-12 lg:!gap-6">
        {tasks.map((task, index) => {
          const Component = componentMapping[task.type];

          return (
            <TaskRow
              key={task.id}
              task={task}
              disabled={isReordering}
              index={index}
              tasksLength={tasks.length}
              handleTaskPositionChange={handleTaskPositionChange}
              updateICL={updateICL}
              t={t}
            >
              <Component
                index={index}
                {...task}
                currentICLId={currentICLId}
                workType={workType}
                userType={userTypeSelector}
                handleDelete={handleDeleteTask}
                handleChangeTaskByKey={handleChangeTaskByKey}
                updateICL={updateICL}
              />
            </TaskRow>
          );
        })}
      </div>

      {isSavedTask && (
        <div className="!mt-5 flex flex-col items-center !gap-3 lg:!mt-6 lg:flex-row">
          <div className="w-full lg:w-1/3">
            <FormControl className="lg:w-1/4" size="small" fullWidth>
              <InputLabel>{t('task type')}</InputLabel>
              <Select
                sx={{ borderRadius: 25, width: '100%' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderWidth: '2px',
                      borderColor: '#52C7FA',
                      marginTop: '6px',
                      borderRadius: '20px',
                    },
                  },
                }}
                value={newTaskType}
                label={t('task type')}
                onChange={event => handleChangeNewTaskType(event.target.value as keyof EmptyTasksType)}
              >
                {taskTypeList.map(it => {
                  return (
                    <MenuItem key={it.name} value={it.value}>
                      {t(it.name)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>

          <button
            className="btn btn-primary more flex w-full items-center gap-[10px] lg:w-2/6"
            onClick={() => {
              handleAddNewTaskToChecklist();
            }}
            disabled={!newTaskType}
          >
            <span className="icon icon-plus"></span>
            <p className="!mb-0 text-lg font-bold">{t('add task')}</p>
          </button>
        </div>
      )}
    </fieldset>
  );
}
